import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { 
  Users, 
  BarChart3, 
  Settings, 
  LogOut, 
  Search, 
  Eye,
  EyeOff,
  Globe, 
  Edit, 
  Edit2,
  Trash2, 
  Eraser,
  Ban, 
  CheckCircle, 
  Check,
  X,
  Key,
  CreditCard,
  RefreshCw,
  Gift,
  TrendingUp,
  DollarSign,
  FolderOpen,
  PieChart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin Auth Context
const AdminAuthContext = React.createContext(null);

export const useAdminAuth = () => React.useContext(AdminAuthContext);

// Admin Login Page
export const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/admin/auth/login`, { email, password });
      sessionStorage.setItem('admin_token', response.data.access_token);
      sessionStorage.setItem('admin_user', JSON.stringify(response.data.user));
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Only @paify.io emails are allowed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/paify-logo.png" alt="Paify" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
          <p className="text-gray-400 mt-2">Access restricted to @paify.io emails</p>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@paify.io"
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-gray-700 border-gray-600 text-white pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center hover:bg-transparent"
                  >
                    {showPassword ? 
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-300" /> : 
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-300" />
                    }
                  </Button>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#e6007e] hover:bg-[#c4006b]"
              >
                {loading ? 'Signing in...' : 'Sign in to Admin'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-sm mt-6">
          <a href="/" className="hover:text-gray-400">← Back to main site</a>
        </p>
      </div>
    </div>
  );
};

// Admin Dashboard
export const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchAnalytics();
  }, [adminToken, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch analytics",
        variant: "destructive"
      });
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-3 text-white border-b-2 border-[#e6007e]"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/admin/users')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users
            </button>
            <button 
              onClick={() => navigate('/admin/reach')}
              className="px-4 py-3 text-white border-b-2 border-transparent flex items-center gap-2"
              data-testid="admin-nav-reach"
              title="Paify Reach — sales outreach"
            >
              <span className="text-[#e6007e] font-semibold">Reach</span>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded text-[#e6007e]" style={{ backgroundColor: 'rgba(230,0,126,0.12)' }}>Sales</span>
            </button>
            <button 
              onClick={() => navigate('/admin/settings')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-8">Platform Analytics</h1>

        {analytics && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Users</p>
                      <p className="text-3xl font-bold text-white">{analytics.users.total}</p>
                      <p className="text-green-400 text-sm mt-1">
                        +{analytics.users.new_signups_30d} last 30 days
                      </p>
                    </div>
                    <Users className="w-12 h-12 text-[#e6007e]" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Revenue</p>
                      <p className="text-3xl font-bold text-white">£{analytics.revenue.total.toLocaleString()}</p>
                      <p className="text-green-400 text-sm mt-1">
                        £{analytics.revenue.monthly.toLocaleString()} this month
                      </p>
                    </div>
                    <DollarSign className="w-12 h-12 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Active Projects</p>
                      <p className="text-3xl font-bold text-white">{analytics.projects.active}</p>
                      <p className="text-gray-500 text-sm mt-1">
                        {analytics.projects.total} total
                      </p>
                    </div>
                    <FolderOpen className="w-12 h-12 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Transactions</p>
                      <p className="text-3xl font-bold text-white">{analytics.revenue.transactions_count}</p>
                      <p className="text-gray-500 text-sm mt-1">
                        Completed payments
                      </p>
                    </div>
                    <CreditCard className="w-12 h-12 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription Breakdown & Payment Structures */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <PieChart className="w-5 h-5 mr-2 text-[#e6007e]" />
                    Subscription Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Standard (Free)</span>
                      <span className="text-white font-semibold">{analytics.users.by_subscription.standard || 0}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-gray-500 h-2 rounded-full" 
                        style={{ width: `${((analytics.users.by_subscription.standard || 0) / analytics.users.total) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Paify Plus</span>
                      <span className="text-white font-semibold">{analytics.users.by_subscription.paidd_plus || 0}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-[#e6007e] h-2 rounded-full" 
                        style={{ width: `${((analytics.users.by_subscription.paidd_plus || 0) / analytics.users.total) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Embedded</span>
                      <span className="text-white font-semibold">{analytics.users.by_subscription.embedded || 0}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${((analytics.users.by_subscription.embedded || 0) / analytics.users.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-green-500" />
                    Popular Payment Structures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics.payment_structures || {}).map(([structure, count]) => (
                      <div key={structure} className="flex items-center justify-between">
                        <span className="text-gray-400 capitalize">
                          {structure.replace(/_/g, ' ')}
                        </span>
                        <span className="text-white font-semibold">{count}</span>
                      </div>
                    ))}
                    {Object.keys(analytics.payment_structures || {}).length === 0 && (
                      <p className="text-gray-500 text-center">No payment data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// Admin Users Management
export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [refundData, setRefundData] = useState({ payment_intent_id: '', amount: '' });
  const [discountData, setDiscountData] = useState({ type: 'upgrade_free', plan: 'paidd_plus', months: 1 });
  const [userPayments, setUserPayments] = useState([]);
  const [editingBusinessName, setEditingBusinessName] = useState(false);
  const [businessNameValue, setBusinessNameValue] = useState('');
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ email: '', business_name: '', contact_name: '' });
  const [savingEditUser, setSavingEditUser] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchUsers();
  }, [adminToken, search, subscriptionFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (subscriptionFilter && subscriptionFilter !== 'all') params.append('subscription', subscriptionFilter);
      
      const response = await axios.get(`${API}/api/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch user details",
        variant: "destructive"
      });
    }
  };

  const fetchUserPayments = async (userId) => {
    try {
      const response = await axios.get(`${API}/api/admin/users/${userId}/payments`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUserPayments(response.data.payments);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      await axios.post(`${API}/api/admin/users/${userId}/suspend`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "User suspended" });
      fetchUsers();
    } catch (err) {
      toast({ title: "Error", description: "Failed to suspend user", variant: "destructive" });
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "User reactivated" });
      fetchUsers();
    } catch (err) {
      toast({ title: "Error", description: "Failed to reactivate user", variant: "destructive" });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to DELETE this user and ALL their data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "User deleted" });
      fetchUsers();
      setShowUserModal(false);
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to delete user", variant: "destructive" });
    }
  };

  const openEditUserModal = (user) => {
    setEditUserForm({
      email: user.email || '',
      business_name: user.business_name || '',
      contact_name: user.contact_name || '',
    });
    setShowEditUserModal(true);
  };

  const handleSaveEditUser = async () => {
    if (!selectedUser?.user?.id) return;
    const payload = {
      email: editUserForm.email.trim().toLowerCase(),
      business_name: editUserForm.business_name.trim(),
      contact_name: editUserForm.contact_name.trim(),
    };
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setSavingEditUser(true);
    try {
      await axios.put(
        `${API}/api/admin/users/${selectedUser.user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Saved", description: "User details updated." });
      setShowEditUserModal(false);
      fetchUserDetails(selectedUser.user.id);
      fetchUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setSavingEditUser(false);
    }
  };

  const handleWipeUserData = async (userId, userEmail) => {
    const expected = `WIPE ${userEmail}`;
    const entered = window.prompt(
      `This will permanently delete ALL projects, invoices, quotes, ` +
      `payments, scheduled jobs, emails and VAT data for ${userEmail}.\n\n` +
      `The login, subscription and settings stay intact.\n\n` +
      `To confirm, type exactly:\n${expected}`
    );
    if (entered !== expected) {
      if (entered !== null) {
        toast({ title: "Cancelled", description: "Confirmation text did not match. Nothing was wiped.", variant: "destructive" });
      }
      return;
    }
    try {
      const response = await axios.post(
        `${API}/api/admin/users/${userId}/wipe-data`,
        { confirm: expected },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const total = response.data?.total_documents_deleted ?? 0;
      toast({
        title: "Wiped",
        description: `${total} document(s) deleted for ${userEmail}. Account preserved.`,
      });
      if (selectedUser?.user?.id === userId) {
        fetchUserDetails(userId);
      }
      fetchUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to wipe user data",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${API}/api/admin/users/${selectedUser.user.id}/reset-password`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Success", description: "Password reset successfully" });
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (err) {
      toast({ title: "Error", description: "Failed to reset password", variant: "destructive" });
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await axios.post(`${API}/api/admin/users/${userId}/impersonate`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      // Store original admin token
      sessionStorage.setItem('admin_token_backup', adminToken);
      
      // Set user token and redirect
      sessionStorage.setItem('token', response.data.access_token);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Open in new tab
      window.open('/', '_blank');
      
      toast({ title: "Success", description: `Logged in as ${response.data.user.email}. Check the new tab.` });
    } catch (err) {
      toast({ title: "Error", description: "Failed to impersonate user", variant: "destructive" });
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/subscription`, 
        { plan },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Success", description: `Subscription updated to ${plan}` });
      fetchUserDetails(userId);
      fetchUsers();
    } catch (err) {
      toast({ title: "Error", description: "Failed to update subscription", variant: "destructive" });
    }
  };

  const handleRefund = async () => {
    if (!refundData.payment_intent_id) {
      toast({ title: "Error", description: "Payment intent ID required", variant: "destructive" });
      return;
    }
    try {
      const response = await axios.post(`${API}/api/admin/users/${selectedUser.user.id}/refund`, 
        refundData,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Success", description: `Refunded £${response.data.amount_refunded}` });
      setShowRefundModal(false);
      setRefundData({ payment_intent_id: '', amount: '' });
      fetchUserPayments(selectedUser.user.id);
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to process refund", variant: "destructive" });
    }
  };

  const handleApplyDiscount = async () => {
    try {
      await axios.post(`${API}/api/admin/users/${selectedUser.user.id}/apply-discount`, 
        discountData,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Success", description: "Discount applied" });
      setShowDiscountModal(false);
      fetchUserDetails(selectedUser.user.id);
    } catch (err) {
      toast({ title: "Error", description: "Failed to apply discount", variant: "destructive" });
    }
  };

  const handleToggleAddon = async (addonName, currentlyActive) => {
    try {
      const action = currentlyActive ? 'deactivate' : 'activate';
      await axios.put(`${API}/api/admin/users/${selectedUser.user.id}/add-ons`, 
        { addon: addonName, action },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const addonDisplay = addonName === 'vat_vault' ? 'VAT Vault' : 
                          addonName === 'invoice_insurance' ? 'Invoice Insurance' : addonName;
      toast({ 
        title: "Success", 
        description: `${addonDisplay} ${action}d successfully` 
      });
      fetchUserDetails(selectedUser.user.id);
    } catch (err) {
      toast({ title: "Error", description: "Failed to update add-on", variant: "destructive" });
    }
  };

  const handleUpdateBusinessName = async () => {
    try {
      await axios.put(`${API}/api/admin/users/${selectedUser.user.id}/business-name`, 
        { business_name: businessNameValue },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ 
        title: "Success", 
        description: "Business name updated successfully" 
      });
      setEditingBusinessName(false);
      fetchUserDetails(selectedUser.user.id);
    } catch (err) {
      toast({ title: "Error", description: "Failed to update business name", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/admin/users')}
              className="px-4 py-3 text-white border-b-2 border-[#e6007e]"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users
            </button>
            <button 
              onClick={() => navigate('/admin/outreach')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Outreach
            </button>
            <button 
              onClick={() => navigate('/admin/settings')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <span className="text-gray-400">{total} total users</span>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by email, business name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
            <SelectTrigger className="w-48 bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="All Subscriptions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subscriptions</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="paidd_plus">Paify Plus</SelectItem>
              <SelectItem value="embedded">Embedded</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Business</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Subscription</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Projects</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">Loading...</td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">No users found</td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-t border-gray-700 hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{user.email}</p>
                          <p className="text-gray-400 text-sm">{user.contact_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{user.business_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.subscription_plan === 'embedded' ? 'bg-purple-500/20 text-purple-300' :
                          user.subscription_plan === 'paidd_plus' ? 'bg-[#e6007e]/20 text-[#e6007e]' :
                          'bg-gray-600/20 text-gray-300'
                        }`}>
                          {user.subscription_plan === 'paidd_plus' ? 'Paify Plus' : 
                           user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.is_active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                        }`}>
                          {user.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{user.project_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => fetchUserDetails(user.id)}
                            className="text-gray-400 hover:text-white"
                            title="Edit user details"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleImpersonate(user.id)}
                            className="text-gray-400 hover:text-blue-400"
                            title="View as user (login as this user)"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleWipeUserData(user.id, user.email)}
                            className="text-gray-400 hover:text-amber-400"
                            title="Wipe ALL projects/invoices/data (keeps the account)"
                            data-testid={`admin-wipe-user-${user.id}`}
                          >
                            <Eraser className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-gray-400 hover:text-red-400"
                            title="Delete user and all their data"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {user.is_active ? (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleSuspendUser(user.id)}
                              className="text-gray-400 hover:text-orange-400"
                              title="Suspend user account"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleReactivateUser(user.id)}
                              className="text-gray-400 hover:text-green-400"
                              title="Reactivate user account"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      {/* User Details Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Email</Label>
                  <div className="flex items-center space-x-2">
                    <p className="text-white" data-testid="admin-user-email">{selectedUser.user.email}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400">Business Name</Label>
                  <p className="text-white" data-testid="admin-user-business-name">{selectedUser.user.business_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-400">Contact Name</Label>
                  <p className="text-white" data-testid="admin-user-contact-name">{selectedUser.user.contact_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-400">Created</Label>
                  <p className="text-white">{new Date(selectedUser.user.created_at).toLocaleDateString()}</p>
                </div>
                <div className="col-span-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditUserModal(selectedUser.user)}
                    className="border-gray-600 text-gray-200 hover:bg-gray-700"
                    data-testid="admin-edit-user-details-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Name &amp; Email
                  </Button>
                </div>
              </div>

              {/* Subscription Management */}
              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-400 mb-2 block">Subscription</Label>
                <div className="flex items-center space-x-4">
                  <Select 
                    value={selectedUser.user.subscription_plan}
                    onValueChange={(value) => handleUpdateSubscription(selectedUser.user.id, value)}
                  >
                    <SelectTrigger className="w-48 bg-gray-700 border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="paidd_plus">Paify Plus</SelectItem>
                      <SelectItem value="embedded">Embedded</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedUser.user.subscription_status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                  }`}>
                    {selectedUser.user.subscription_status}
                  </span>
                </div>
              </div>

              {/* Add-ons */}
              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-400 mb-3 block">Add-ons</Label>
                <div className="space-y-2">
                  {/* VAT Vault Toggle */}
                  <div className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        selectedUser.user.add_ons?.includes('vat_vault') ? 'bg-green-500' : 'bg-gray-500'
                      }`}></div>
                      <span className="text-white text-sm">VAT Vault</span>
                    </div>
                    <Button
                      size="sm"
                      variant={selectedUser.user.add_ons?.includes('vat_vault') ? "destructive" : "default"}
                      onClick={() => handleToggleAddon('vat_vault', selectedUser.user.add_ons?.includes('vat_vault'))}
                      className={selectedUser.user.add_ons?.includes('vat_vault') 
                        ? "bg-red-600 hover:bg-red-700 text-white text-xs" 
                        : "bg-green-600 hover:bg-green-700 text-white text-xs"}
                    >
                      {selectedUser.user.add_ons?.includes('vat_vault') ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                  
                  {/* Invoice Insurance Toggle - Coming Soon */}
                  <div className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span className="text-white text-sm">Invoice Insurance</span>
                    </div>
                    <Button
                      size="sm"
                      disabled
                      className="bg-gray-500 text-gray-300 text-xs cursor-not-allowed hover:bg-gray-500"
                    >
                      Coming soon
                    </Button>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 border-t border-gray-700 pt-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Total Projects</p>
                  <p className="text-2xl font-bold text-white">{selectedUser.stats.total_projects}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold text-white">£{selectedUser.stats.total_revenue.toLocaleString()}</p>
                </div>
              </div>

              {/* Recent Projects */}
              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-400 mb-2 block">Recent Projects</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedUser.projects.slice(0, 5).map(project => (
                    <div key={project.id} className="flex items-center justify-between bg-gray-700 rounded px-3 py-2">
                      <div>
                        <p className="text-white text-sm">{project.title}</p>
                        <p className="text-gray-400 text-xs">{project.client_name}</p>
                      </div>
                      <span className="text-gray-300">£{project.total_amount}</span>
                    </div>
                  ))}
                  {selectedUser.projects.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No projects</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 border-t border-gray-700 pt-4">
                <Button 
                  size="sm"
                  onClick={() => handleImpersonate(selectedUser.user.id)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Login as User
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPasswordModal(true)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    fetchUserPayments(selectedUser.user.id);
                    setShowRefundModal(true);
                  }}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Process Refund
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDiscountModal(true)}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Apply Discount
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => handleWipeUserData(selectedUser.user.id, selectedUser.user.email)}
                  className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                  data-testid="admin-modal-wipe-user-data"
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  Wipe All Data
                </Button>
                <Button 
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteUser(selectedUser.user.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="bg-gray-700 border-gray-600 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center hover:bg-transparent"
                >
                  {showNewPassword ? 
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-300" /> : 
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-300" />
                  }
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#e6007e] hover:bg-[#c4006b]">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Details Modal */}
      <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Email address</Label>
              <Input
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="user@example.com"
                data-testid="admin-edit-user-email-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Changing the email updates the login. The user must use the new address next time they sign in.
              </p>
            </div>
            <div>
              <Label className="text-gray-400">Business name</Label>
              <Input
                value={editUserForm.business_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, business_name: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Horizon Digital Studio"
                data-testid="admin-edit-user-business-input"
              />
            </div>
            <div>
              <Label className="text-gray-400">Contact name</Label>
              <Input
                value={editUserForm.contact_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, contact_name: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Jane Smith"
                data-testid="admin-edit-user-contact-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditUserModal(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditUser}
              disabled={savingEditUser}
              className="bg-[#e6007e] hover:bg-[#cc006f] text-white"
              data-testid="admin-edit-user-save-btn"
            >
              {savingEditUser ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Select Payment to Refund</Label>
              <Select 
                value={refundData.payment_intent_id}
                onValueChange={(value) => setRefundData({...refundData, payment_intent_id: value})}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600">
                  <SelectValue placeholder="Select a payment" />
                </SelectTrigger>
                <SelectContent>
                  {userPayments.map(payment => (
                    <SelectItem key={payment.payment_intent_id} value={payment.payment_intent_id || ''}>
                      {payment.project_title} - {payment.milestone_title} (£{payment.amount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Refund Amount (optional - leave blank for full refund)</Label>
              <Input
                type="number"
                value={refundData.amount}
                onChange={(e) => setRefundData({...refundData, amount: e.target.value})}
                placeholder="Full refund if empty"
                className="bg-gray-700 border-gray-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRefundModal(false)}>Cancel</Button>
            <Button onClick={handleRefund} className="bg-red-600 hover:bg-red-700">
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Plan</Label>
              <Select 
                value={discountData.plan}
                onValueChange={(value) => setDiscountData({...discountData, plan: value})}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paidd_plus">Paify Plus</SelectItem>
                  <SelectItem value="embedded">Embedded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Free Months</Label>
              <Input
                type="number"
                value={discountData.months}
                onChange={(e) => setDiscountData({...discountData, months: parseInt(e.target.value) || 1})}
                min="1"
                max="12"
                className="bg-gray-700 border-gray-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDiscountModal(false)}>Cancel</Button>
            <Button onClick={handleApplyDiscount} className="bg-[#e6007e] hover:bg-[#c4006b]">
              Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Platform Settings Page
export const AdminSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    stripe_api_key: '',
    stripe_secret_key: '',
    stripe_publishable_key: '',
    stripe_webhook_secret: '',
    frontend_url: ''
  });
  const [showKeys, setShowKeys] = useState({
    stripe_api_key: false,
    stripe_secret_key: false,
    stripe_publishable_key: false,
    stripe_webhook_secret: false,
    frontend_url: false
  });
  const [feeConfig, setFeeConfig] = useState(null);
  const [feeForm, setFeeForm] = useState({ free_percent: '', paid_percent: '' });
  const [savingFees, setSavingFees] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchSettings();
    fetchFeeConfig();
  }, [adminToken, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/platform-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSettings(response.data);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch settings",
        variant: "destructive"
      });
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const handleSave = async () => {
    // Only send non-empty fields
    const dataToSend = {};
    if (formData.stripe_api_key) dataToSend.stripe_api_key = formData.stripe_api_key;
    if (formData.stripe_secret_key) dataToSend.stripe_secret_key = formData.stripe_secret_key;
    if (formData.stripe_publishable_key) dataToSend.stripe_publishable_key = formData.stripe_publishable_key;
    if (formData.stripe_webhook_secret) dataToSend.stripe_webhook_secret = formData.stripe_webhook_secret;
    if (formData.frontend_url) dataToSend.frontend_url = formData.frontend_url;

    if (Object.keys(dataToSend).length === 0) {
      toast({
        title: "No changes",
        description: "Please enter at least one value to update",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/api/admin/platform-settings`, dataToSend, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      if (response.data.test_result) {
        setTestResult(response.data.test_result);
      }
      
      toast({
        title: "Success",
        description: "Settings updated successfully",
      });
      
      // Clear form and refresh
      setFormData({
        stripe_api_key: '',
        stripe_secret_key: '',
        stripe_publishable_key: '',
        stripe_webhook_secret: '',
        frontend_url: ''
      });
      fetchSettings();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to update settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/api/admin/platform-settings/test-stripe`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTestResult(response.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const fetchFeeConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/platform-fees`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setFeeConfig(response.data);
      setFeeForm({
        free_percent: String(response.data.free_percent ?? ''),
        paid_percent: String(response.data.paid_percent ?? '')
      });
    } catch (err) {
      // non-fatal — surface only if it's an auth problem
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const handleSaveFees = async () => {
    const free = parseFloat(feeForm.free_percent);
    const paid = parseFloat(feeForm.paid_percent);
    if (isNaN(free) || isNaN(paid) || free < 0 || paid < 0 || free > 100 || paid > 100) {
      toast({
        title: "Invalid values",
        description: "Both fees must be numbers between 0 and 100.",
        variant: "destructive"
      });
      return;
    }
    setSavingFees(true);
    try {
      await axios.put(
        `${API}/api/admin/platform-fees`,
        { free_percent: free, paid_percent: paid },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: "Saved", description: "Platform fees updated." });
      fetchFeeConfig();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to update fees",
        variant: "destructive"
      });
    } finally {
      setSavingFees(false);
    }
  };

  const toggleShowKey = (keyName) => {
    setShowKeys(prev => ({ ...prev, [keyName]: !prev[keyName] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  const KeyField = ({ label, keyName, placeholder, currentValue }) => (
    <div className="space-y-2">
      <Label className="text-gray-300 flex items-center justify-between">
        <span>{label}</span>
        <span className="text-xs text-gray-500">Current: {currentValue || 'Not set'}</span>
      </Label>
      <div className="relative">
        <Input
          type={showKeys[keyName] ? "text" : "password"}
          value={formData[keyName]}
          onChange={(e) => setFormData({...formData, [keyName]: e.target.value})}
          placeholder={placeholder}
          className="bg-gray-700 border-gray-600 text-white font-mono pr-10"
        />
        <button
          type="button"
          onClick={() => toggleShowKey(keyName)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
        >
          {showKeys[keyName] ? <X className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/admin/users')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users
            </button>
            <button 
              onClick={() => navigate('/admin/outreach')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent"
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Outreach
            </button>
            <button 
              onClick={() => navigate('/admin/settings')}
              className="px-4 py-3 text-white border-b-2 border-[#e6007e]"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">Platform Settings</h1>
        <p className="text-gray-400 mb-8">
          Manage your Stripe integration keys. Keys are loaded from: <span className="text-[#e6007e] font-semibold">{settings?.source || 'environment'}</span>
          {settings?.last_updated && (
            <span className="ml-2 text-sm">
              (Last updated: {new Date(settings.last_updated).toLocaleString()} by {settings.updated_by})
            </span>
          )}
        </p>

        {/* Stripe Configuration */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Stripe API Keys
            </CardTitle>
            <CardDescription className="text-gray-400">
              Enter new keys to update. Leave empty to keep the current value.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <KeyField 
                label="API Key" 
                keyName="stripe_api_key" 
                placeholder="sk_live_..." 
                currentValue={settings?.stripe_api_key}
              />
              <KeyField 
                label="Secret Key" 
                keyName="stripe_secret_key" 
                placeholder="sk_live_..." 
                currentValue={settings?.stripe_secret_key}
              />
              <KeyField 
                label="Publishable Key" 
                keyName="stripe_publishable_key" 
                placeholder="pk_live_..." 
                currentValue={settings?.stripe_publishable_key}
              />
              <KeyField 
                label="Webhook Secret" 
                keyName="stripe_webhook_secret" 
                placeholder="whsec_..." 
                currentValue={settings?.stripe_webhook_secret}
              />
            </div>
            
            <div className="pt-4 flex flex-wrap items-center gap-4 border-t border-gray-700">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#e6007e] hover:bg-[#c4006b]"
              >
                {saving ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Save Changes</>
                )}
              </Button>
              
              <Button 
                onClick={handleTestConnection} 
                disabled={testing}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                {testing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                ) : (
                  <><Key className="w-4 h-4 mr-2" /> Test Connection</>
                )}
              </Button>
              
              {testResult && (
                <div className={`px-4 py-2 rounded text-sm ${testResult.success ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                  {testResult.success ? (
                    <span><CheckCircle className="w-4 h-4 inline mr-2" />Connected to {testResult.account_id} ({testResult.account_name})</span>
                  ) : (
                    <span><X className="w-4 h-4 inline mr-2" />{testResult.error}</span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Platform Fees Configuration */}
        <Card className="bg-gray-800 border-gray-700 mb-6" data-testid="admin-platform-fees-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Paify Platform Fees
            </CardTitle>
            <CardDescription className="text-gray-400">
              Editable platform commission Paify charges per transaction. The fee is collected via Stripe Connect <code className="text-[#e6007e]">application_fee_amount</code> and deducted from the service provider&apos;s Stripe payout — Paify never holds or processes the client&apos;s funds. Applies to Pay Upfront, 50/50, Monthly Instalments, Credit Card and BNPL (when enabled).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center justify-between">
                  <span>Free plan fee (%)</span>
                  <span className="text-xs text-gray-500">
                    Current: {feeConfig?.free_percent ?? '—'}% · Default: {feeConfig?.defaults?.free_percent ?? 3}%
                  </span>
                </Label>
                <Input
                  data-testid="fee-free-percent-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={feeForm.free_percent}
                  onChange={(e) => setFeeForm({ ...feeForm, free_percent: e.target.value })}
                  placeholder="3.0"
                  className="bg-gray-700 border-gray-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Applied to users on the <span className="text-gray-300 font-semibold">Standard (free)</span> plan.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center justify-between">
                  <span>Paid plan fee (%)</span>
                  <span className="text-xs text-gray-500">
                    Current: {feeConfig?.paid_percent ?? '—'}% · Default: {feeConfig?.defaults?.paid_percent ?? 2}%
                  </span>
                </Label>
                <Input
                  data-testid="fee-paid-percent-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={feeForm.paid_percent}
                  onChange={(e) => setFeeForm({ ...feeForm, paid_percent: e.target.value })}
                  placeholder="2.0"
                  className="bg-gray-700 border-gray-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Applied to users on <span className="text-gray-300 font-semibold">Paify Plus / Embedded</span> plans.
                </p>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-4 border-t border-gray-700">
              <Button
                data-testid="save-platform-fees-btn"
                onClick={handleSaveFees}
                disabled={savingFees}
                className="bg-[#e6007e] hover:bg-[#c4006b]"
              >
                {savingFees ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Save Fees</>
                )}
              </Button>
              {feeConfig?.last_updated && (
                <span className="text-xs text-gray-500">
                  Last updated {new Date(feeConfig.last_updated).toLocaleString()} by {feeConfig.updated_by}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Frontend URL Configuration */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Frontend URL
            </CardTitle>
            <CardDescription className="text-gray-400">
              Configure the public URL for your Paify application.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center justify-between">
                <span>Frontend URL (for payment links)</span>
                <span className="text-xs text-gray-500">Current: {settings?.frontend_url || 'Not set'}</span>
              </Label>
              <Input
                type="text"
                value={formData.frontend_url}
                onChange={(e) => setFormData({...formData, frontend_url: e.target.value})}
                placeholder="https://app.paify.io"
                className="bg-gray-700 border-gray-600 text-white"
              />
              <p className="text-xs text-gray-500">
                This URL is used for all payment links sent to clients. Set this to your production domain (e.g., https://app.paify.io).
              </p>
            </div>
            
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#e6007e] hover:bg-[#c4006b]"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Save URL</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="bg-blue-900/20 border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Key className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h3 className="text-blue-400 font-semibold mb-1">How it works</h3>
                <p className="text-blue-300/70 text-sm">
                  Keys saved here are stored in the database and take priority over environment variables. 
                  This allows you to update Stripe keys without redeploying the application.
                  After saving, test the connection to verify your keys are working correctly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminLoginPage;
